import type { RefObject } from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faTimes } from "@fortawesome/free-solid-svg-icons"
import { Plus, Search } from "lucide-react"
import CustomSelect from "../../../../pages/ProjectDocumentation/components/CustomSelect.tsx"
import type { CatalogMaterial } from "../../../../types/calculadoraDeMateriales.ts"
import { SEARCH_RESULTS_LIMIT } from "../../../../constants/calculadoraDeMateriales.ts"
import styles from "../CalculadoraDeMateriales.module.scss"

export type CategorySelectOption =
  | { value: string; label: string; description?: string; variant?: "accent" }
  | { value: string; label: string }

type CalculadoraCatalogToolbarProps = {
  categoryOptions: CategorySelectOption[]
  selectedCategory: string
  toggleCategory: (categoryId: string) => void
  activeCategoryLabel: string | null
  onClearCategoryFilter: () => void
  searchPlaceholder: string
  searchInputRef: RefObject<HTMLInputElement | null>
  searchInputActive: boolean
  searchTerm: string
  handleSearchChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  clearSearch: () => void
  filteredMaterials: (CatalogMaterial & { category: string; categoryId: string })[]
  visibleMaterials: (CatalogMaterial & { category: string; categoryId: string })[]
  searchHighlightIndex: number
  setSearchHighlightIndex: React.Dispatch<React.SetStateAction<number>>
  toggleMaterial: (material: CatalogMaterial & { category?: string }, categoryId: string) => void
  hasMoreResults: boolean
  searchResultsExpanded: boolean
  setSearchResultsExpanded: (v: boolean) => void
  setShowAddCustomPopup: (v: boolean) => void
  setSearchTerm: (v: string) => void
}

const CalculadoraCatalogToolbar = ({
  categoryOptions,
  selectedCategory,
  toggleCategory,
  activeCategoryLabel,
  onClearCategoryFilter,
  searchPlaceholder,
  searchInputRef,
  searchInputActive,
  searchTerm,
  handleSearchChange,
  clearSearch,
  filteredMaterials,
  visibleMaterials,
  searchHighlightIndex,
  setSearchHighlightIndex,
  toggleMaterial,
  hasMoreResults,
  searchResultsExpanded,
  setSearchResultsExpanded,
  setShowAddCustomPopup,
  setSearchTerm,
}: CalculadoraCatalogToolbarProps) => {
  const showCategoryFilter = Boolean(activeCategoryLabel)

  return (
    <div className={styles.container_body_header}>
      <div className={styles.container_body_header_inputs}>
        <div
          className={`${styles.container_body_header_searchContainer} ${
            searchInputActive && styles.container_body_header_searchContainer_active
          }`}
        >
          <div className={styles.container_body_header_searchContainer_icon}>
            <Search size={20} />
            <input
              ref={searchInputRef}
              type="text"
              placeholder={searchPlaceholder}
              value={searchTerm}
              onChange={handleSearchChange}
              className={styles.container_body_header_searchContainer_input}
              aria-describedby={showCategoryFilter ? "calc-category-search-scope" : undefined}
            />
          </div>
          {searchTerm && <FontAwesomeIcon icon={faTimes} className={styles.clearIcon} onClick={clearSearch} />}
          {searchTerm && filteredMaterials.length > 0 && (
            <ul className={styles.container_body_header_searchContainer_filteredResults}>
              {visibleMaterials.map((material, index) => (
                <li
                  className={`${styles.container_body_header_searchContainer_filteredResults_item} ${index === searchHighlightIndex ? styles.container_body_header_searchContainer_filteredResults_item_highlighted : ""}`}
                  key={material.id}
                  onClick={() => toggleMaterial(material, material.categoryId)}
                  onMouseEnter={() => setSearchHighlightIndex(index)}
                >
                  <span className={styles.container_body_header_searchContainer_filteredResults_item_material}>
                    {material.name}
                  </span>
                  <span className={styles.container_body_header_searchContainer_filteredResults_item_category}>
                    {material.category}
                  </span>
                </li>
              ))}
              {hasMoreResults && !searchResultsExpanded && (
                <li
                  className={`${styles.container_body_header_searchContainer_filteredResults_item} ${styles.container_body_header_searchContainer_filteredResults_item_verMas} ${visibleMaterials.length === searchHighlightIndex ? styles.container_body_header_searchContainer_filteredResults_item_highlighted : ""}`}
                  onClick={() => setSearchResultsExpanded(true)}
                  onMouseEnter={() => setSearchHighlightIndex(visibleMaterials.length)}
                >
                  <span className={styles.container_body_header_searchContainer_filteredResults_item_material}>
                    Ver más ({filteredMaterials.length - SEARCH_RESULTS_LIMIT} restantes)
                  </span>
                </li>
              )}
            </ul>
          )}
          {searchTerm && filteredMaterials.length === 0 && (
            <ul className={styles.container_body_header_searchContainer_filteredResults}>
              <li
                className={`${styles.container_body_header_searchContainer_filteredResults_item} ${styles.container_body_header_searchContainer_filteredResults_item_create} ${searchHighlightIndex === 0 ? styles.container_body_header_searchContainer_filteredResults_item_highlighted : ""}`}
                onClick={() => {
                  setShowAddCustomPopup(true)
                  setSearchTerm("")
                }}
                onMouseEnter={() => setSearchHighlightIndex(0)}
              >
                <Plus size={16} className={styles.container_body_header_searchContainer_filteredResults_item_icon} />
                <div className={styles.container_body_header_searchContainer_filteredResults_item_createContent}>
                  <span className={styles.container_body_header_searchContainer_filteredResults_item_material}>
                    Agregar material manualmente
                  </span>
                  <span className={styles.container_body_header_searchContainer_filteredResults_item_category}>
                    Agregá un material que no esté en la lista
                  </span>
                </div>
              </li>
            </ul>
          )}
        </div>
        <div className={styles.container_body_header_categories}>
          <div className={styles.container_body_header_categories_dropdown}>
            <CustomSelect
              value={selectedCategory}
              onChange={toggleCategory}
              options={categoryOptions}
              placeholder="Explorar por rubro"
              searchable
              searchPlaceholder="Buscar categoría..."
            />
          </div>
        </div>
      </div>
      {showCategoryFilter ? (
        <p id="calc-category-search-scope" className={styles.container_body_header_categoryFilterHint} role="status">
          <span className={styles.container_body_header_categoryFilterHint_label}>
            Buscando en <strong>{activeCategoryLabel}</strong>
          </span>
          <button
            type="button"
            className={styles.container_body_header_categoryFilterHint_link}
            onClick={onClearCategoryFilter}
          >
            Quitar filtro
          </button>
        </p>
      ) : null}
    </div>
  )
}

export default CalculadoraCatalogToolbar
